import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Shield, AlertTriangle } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-[#F4F1EA] py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#E07A5F] rounded-full flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              OuttaTouch
            </span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-4xl font-bold text-[#3D405B] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
          Privacy Policy
        </h1>
        <p className="text-[#6D6F7C] mb-8">Last updated: January 2025</p>

        <div className="prose prose-lg max-w-none text-[#3D405B]">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              1. Introduction
            </h2>
            <p className="mb-4">
              <strong>MidVantage Labs</strong> ("Company", "we", "us", or "our") operates OuttaTouch 
              (the "Service") at <a href="https://tryouttatouch.com" className="text-[#E07A5F] hover:underline">tryouttatouch.com</a>. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
            </p>
            <p>
              We are committed to protecting your privacy and ensuring you understand how your data is handled. 
              Please read this policy carefully. By using OuttaTouch, you consent to the practices described in this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              2. Information We Collect
            </h2>
            
            <h3 className="text-xl font-semibold text-[#3D405B] mb-3">Personal Information</h3>
            <p className="mb-4">When you register for an account, we collect:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Name and email address</li>
              <li>Password (stored securely using industry-standard bcrypt hashing)</li>
              <li>Profile picture (if provided via Google sign-in)</li>
              <li>Timestamp of your agreement to our Terms and Privacy Policy</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#3D405B] mb-3">Payment Information</h3>
            <p className="mb-4">
              When you subscribe to a paid plan, payment information is collected and processed directly by 
              <strong> Stripe, Inc.</strong> We do not store your credit card numbers, bank account details, 
              or other sensitive payment information on our servers. We only receive:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Confirmation of successful payment</li>
              <li>Subscription status and billing dates</li>
              <li>Last 4 digits of your payment method (for display purposes only)</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#3D405B] mb-3">Contact Information You Provide</h3>
            <p className="mb-4">When you use our contact management features, we store:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Contact names, phone numbers, and email addresses</li>
              <li>Birthday, anniversary, and other important dates</li>
              <li>Notes and call history you create</li>
              <li>Imported contacts from Google (with your explicit permission)</li>
              <li>Gift sending history and preferences</li>
            </ul>
            
            <div className="bg-[#E07A5F]/10 rounded-2xl p-6 mb-4 border border-[#E07A5F]/30">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-[#E07A5F]" />
                <span className="font-semibold text-[#3D405B]">Your Responsibility</span>
              </div>
              <p className="text-[#3D405B] text-sm">
                You are responsible for ensuring you have appropriate consent to store personal information 
                about other individuals. See our Terms of Service for more details on your responsibilities.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-[#3D405B] mb-3">Usage Information</h3>
            <p className="mb-4">We automatically collect:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Device information and browser type</li>
              <li>IP address and general location (country/region)</li>
              <li>App usage patterns and feature interactions</li>
              <li>Error logs for troubleshooting</li>
              <li>Referral source and marketing attribution</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              3. How We Use Your Information
            </h2>
            <p className="mb-4">We use collected information to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Provide, maintain, and improve the Service</li>
              <li>Process subscription payments and send related notifications</li>
              <li>Send reminder notifications for staying in touch with your contacts</li>
              <li>Provide AI-powered features such as call summaries (for Plus and Family plans)</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Send promotional communications (only with your explicit consent)</li>
              <li>Detect, prevent, and address technical issues, fraud, and security threats</li>
              <li>Generate anonymized analytics to improve the Service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              4. AI Features and Data Processing
            </h2>
            <p className="mb-4">
              OuttaTouch uses artificial intelligence to provide features such as post-call summaries 
              (available on Plus and Family plans).
            </p>
            <div className="bg-[#81B29A]/10 rounded-2xl p-6 mb-4 border border-[#81B29A]/30">
              <h4 className="font-bold text-[#3D405B] mb-2">How AI Processes Your Data</h4>
              <ul className="list-disc pl-6 space-y-2 text-[#3D405B]">
                <li>Contact names and your notes may be sent to OpenAI's API to generate summaries</li>
                <li>Data sent to AI providers is used solely to generate your requested content</li>
                <li>We do not use your data to train AI models</li>
                <li>AI-generated content is not stored by the AI provider after generation</li>
              </ul>
            </div>
            <p>
              For more information about how our AI provider handles data, please refer to 
              <a href="https://openai.com/privacy" className="text-[#E07A5F] hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                OpenAI's Privacy Policy
              </a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              5. Third-Party Processors and Services
            </h2>
            <p className="mb-4">
              We share your information with the following categories of third-party service providers who 
              help us operate the Service:
            </p>
            
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse border border-[#F4F1EA] text-sm">
                <thead>
                  <tr className="bg-[#F4F1EA]">
                    <th className="border border-[#F4F1EA] p-3 text-left">Provider</th>
                    <th className="border border-[#F4F1EA] p-3 text-left">Purpose</th>
                    <th className="border border-[#F4F1EA] p-3 text-left">Data Shared</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-[#F4F1EA] p-3 font-medium">Stripe</td>
                    <td className="border border-[#F4F1EA] p-3">Payment processing</td>
                    <td className="border border-[#F4F1EA] p-3">Payment details, email, billing info</td>
                  </tr>
                  <tr className="bg-[#FDFBF7]">
                    <td className="border border-[#F4F1EA] p-3 font-medium">OpenAI</td>
                    <td className="border border-[#F4F1EA] p-3">AI features (call summaries)</td>
                    <td className="border border-[#F4F1EA] p-3">Contact names, notes, conversation context</td>
                  </tr>
                  <tr>
                    <td className="border border-[#F4F1EA] p-3 font-medium">Google</td>
                    <td className="border border-[#F4F1EA] p-3">Authentication, contact import</td>
                    <td className="border border-[#F4F1EA] p-3">Email, name, profile picture, contacts (if imported)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <p className="mb-4">
              We may also share information:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>For legal requirements:</strong> If required by law, court order, or to protect our rights</li>
              <li><strong>In business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="font-semibold">We never sell your personal information to third parties for marketing purposes.</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              6. Google Contact Import
            </h2>
            <p className="mb-4">
              When you choose to import contacts from Google, we:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Request only the permissions necessary to read your contacts (read-only access)</li>
              <li>Store imported contact data securely in our database, associated with your account</li>
              <li>Never share your Google contacts with third parties (except as needed for gift delivery)</li>
              <li>Allow you to delete imported contacts at any time</li>
            </ul>
            <p>
              You can revoke our access to your Google account at any time through your 
              <a href="https://myaccount.google.com/permissions" className="text-[#E07A5F] hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                Google Account settings
              </a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              7. Data Security
            </h2>
            <div className="bg-[#81B29A]/10 rounded-2xl p-6 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-[#81B29A]" />
                <span className="font-semibold text-[#3D405B]">Our Security Measures</span>
              </div>
              <ul className="list-disc pl-6 space-y-2 text-[#3D405B]">
                <li>Encryption of all data in transit using HTTPS/TLS</li>
                <li>Secure password hashing using bcrypt</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Role-based access controls for internal systems</li>
                <li>Secure payment processing through PCI-DSS compliant Stripe</li>
                <li>Encrypted database backups</li>
              </ul>
            </div>
            <p>
              While we implement robust security measures, no method of transmission over the Internet 
              is 100% secure. We cannot guarantee absolute security of your data, but we are committed 
              to protecting it using industry best practices.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              8. Your Rights (GDPR/CCPA)
            </h2>
            <p className="mb-4">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data we hold</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data ("right to be forgotten")</li>
              <li><strong>Portability:</strong> Receive your data in a portable, machine-readable format</li>
              <li><strong>Opt-out:</strong> Opt out of marketing communications at any time</li>
              <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
              <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
            </ul>
            <p>
              To exercise these rights, contact us at <a href="mailto:privacy@tryouttatouch.com" className="text-[#E07A5F] hover:underline">privacy@tryouttatouch.com</a>. 
              We will respond to your request within 30 days.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              9. Data Retention
            </h2>
            <p className="mb-4">
              We retain your personal data for as long as your account is active or as needed to provide services:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Account data:</strong> Retained until you delete your account</li>
              <li><strong>Contact data:</strong> Retained until you delete the contact or your account</li>
              <li><strong>Payment records:</strong> Retained for 7 years for legal/tax compliance</li>
              <li><strong>Usage logs:</strong> Retained for 90 days for troubleshooting</li>
            </ul>
            <p className="mb-4">
              After account deletion, we may retain certain information for:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Legal compliance and audit purposes</li>
              <li>Fraud prevention and security</li>
              <li>Resolving disputes</li>
            </ul>
            <p>
              Most data is permanently deleted within 90 days of account deletion.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              10. Cookies and Tracking
            </h2>
            <p className="mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Essential cookies:</strong> Keep you signed in and remember your preferences</li>
              <li><strong>Analytics cookies:</strong> Understand usage patterns and improve our Service</li>
              <li><strong>Functional cookies:</strong> Enable features like dark mode preferences</li>
            </ul>
            <p>
              You can control cookies through your browser settings. Disabling essential cookies may affect 
              your ability to use certain features of the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              11. Children's Privacy
            </h2>
            <p>
              Our Service is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If we discover that a child under 13 has provided 
              us with personal information, we will delete it immediately. If you believe we have collected 
              information from a child under 13, please contact us at <a href="mailto:privacy@tryouttatouch.com" className="text-[#E07A5F] hover:underline">privacy@tryouttatouch.com</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              12. International Data Transfers
            </h2>
            <p>
              Your information may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place for such transfers in compliance with applicable 
              data protection laws, including standard contractual clauses where required.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              13. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy periodically. We will notify you of any material changes 
              by posting the new policy on this page, updating the "Last updated" date, and sending you an 
              email notification for significant changes. We encourage you to review this policy regularly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              14. Contact Us
            </h2>
            <p className="mb-4">
              For questions about this Privacy Policy, data requests, or our data practices, contact us:
            </p>
            <div className="bg-[#F4F1EA] rounded-2xl p-6">
              <p className="font-bold text-[#3D405B] mb-2">MidVantage Labs - Privacy Team</p>
              <div className="space-y-2 text-[#6D6F7C]">
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> privacy@tryouttatouch.com
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> support@tryouttatouch.com (general inquiries)
                </p>
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> tryouttatouch.com
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-[#F4F1EA] py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-[#6D6F7C]">
          <p>© 2025 MidVantage Labs. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/terms" className="hover:text-[#E07A5F]">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-[#E07A5F]">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
